import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { 
  Users, 
  BookOpen, 
  DollarSign, 
  TrendingUp,
  Eye,
  Plus,
  BarChart3
} from 'lucide-react';
import { coursesApi, categoriesApi, analyticsApi, studentsApi } from '@/services/api';
import { Course, Category } from '@/types';

interface DashboardStats {
  totalCourses: number;
  totalStudents: number;
  totalRevenue: number;
  avgRating: number;
}

export const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalCourses: 0,
    totalStudents: 0,
    totalRevenue: 0,
    avgRating: 0,
  });
  const [recentCourses, setRecentCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        console.log('🚀 Starting dashboard data fetch...');
        const [coursesResponse, categoriesResponse, metricsResponse, studentsResponse] = await Promise.all([
          coursesApi.getAll({ limit: 5 }),
          categoriesApi.getAll(),
          analyticsApi.getMetrics(),
          studentsApi.getStats()
        ]);
        console.log('✅ All requests completed');

        // Handle courses data
        let courses: any[] = [];
        if ((coursesResponse.data as any).success) {
          courses = (coursesResponse.data as any).data.courses || [];
        } else if ((coursesResponse.data as any).courses) {
          courses = (coursesResponse.data as any).courses;
        }

        // Handle categories data
        let categoriesData: any[] = [];
        if (Array.isArray(categoriesResponse.data)) {
          categoriesData = categoriesResponse.data;
        } else if ((categoriesResponse.data as any).success) {
          categoriesData = (categoriesResponse.data as any).data || [];
        }

        // Handle metrics from analytics
        let totalStudents = 0;
        let totalRevenue = 0;
        if (metricsResponse.data.success) {
          const metrics = metricsResponse.data.data;
          totalStudents = metrics.totalStudents || 0;
          totalRevenue = metrics.totalRevenue || 0;
        } else if (studentsResponse.data.success) {
          totalStudents = studentsResponse.data.data.total || 0;
        }

        // Calculate average rating from courses
        const avgRating = courses.length > 0 
          ? courses.reduce((acc: number, course: any) => acc + (Number(course.rating) || 0), 0) / courses.length 
          : 0;

        setStats({
          totalCourses: courses.length,
          totalStudents,
          totalRevenue,
          avgRating: Math.round(avgRating * 10) / 10,
        });

        setRecentCourses(courses);
        setCategories(categoriesData);
      } catch (error) {
        console.error('❌ Error fetching dashboard data:', error);
        console.error('❌ Error details:', {
          message: (error as any)?.message,
          response: (error as any)?.response?.data,
          status: (error as any)?.response?.status
        });
        alert('Error al cargar datos del dashboard. Revisa la conexión con el servidor.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const statCards = [
    {
      title: 'Total Estudiantes',
      value: stats.totalStudents.toLocaleString(),
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      change: '+12.5%',
    },
    {
      title: 'Cursos Activos',
      value: (stats.totalCourses || 0).toString(),
      icon: BookOpen,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      change: '+3 nuevos',
    },
    {
      title: 'Ingresos Totales',
      value: `$${stats.totalRevenue.toLocaleString()}`,
      icon: DollarSign,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      change: '+18.2%',
    },
    {
      title: 'Rating Promedio',
      value: (stats.avgRating || 0).toString(),
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      change: '+0.2',
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-20 bg-gray-200 rounded"></div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600">Resumen de la plataforma de e-learning</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3 w-full sm:w-auto">
          <Button variant="outline" leftIcon={BarChart3} className="w-full sm:w-auto">
            Ver Reportes
          </Button>
          <Button leftIcon={Plus} className="w-full sm:w-auto">
            Nuevo Curso
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statCards.map((stat, index) => (
          <Card key={index}>
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{stat.value}</p>
                <p className="text-xs sm:text-sm text-green-600 mt-1">{stat.change}</p>
              </div>
              <div className={`p-2 sm:p-3 rounded-full ${stat.bgColor} flex-shrink-0 ml-2`}>
                <stat.icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.color}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Courses */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Cursos Recientes</CardTitle>
            <CardDescription className="text-sm">Últimos cursos creados en la plataforma</CardDescription>
          </CardHeader>

          <div className="space-y-3 sm:space-y-4 max-h-96 overflow-y-auto">
            {(recentCourses || []).map((course) => (
              <div key={course.id} className="flex items-center space-x-3 sm:space-x-4 p-3 hover:bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  <div
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-white text-xs sm:text-sm font-medium"
                    style={{ backgroundColor: course.category.color || '#3B82F6' }}
                  >
                    {course.title.charAt(0)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {course.title}
                  </p>
                  <p className="text-xs text-gray-500">
                    {course.category.name} • {course.studentsCount} estudiantes
                  </p>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                  <span className="text-xs sm:text-sm font-medium text-gray-900">
                    ${course.price}
                  </span>
                  <Button variant="ghost" size="sm" className="p-1 sm:p-2">
                    <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Categories Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Categorías</CardTitle>
            <CardDescription className="text-sm">Distribución de cursos por categoría</CardDescription>
          </CardHeader>

          <div className="space-y-3 sm:space-y-4 max-h-96 overflow-y-auto">
            {categories.map((category) => (
              <div key={category.id} className="flex items-center space-x-3 sm:space-x-4 p-3 hover:bg-gray-50 rounded-lg">
                <div
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center text-white"
                  style={{ backgroundColor: category.color || '#3B82F6' }}
                >
                  <span className="text-xs sm:text-sm font-medium">
                    {category.name.charAt(0)}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {category.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {category._count?.courses || 0} cursos
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="w-10 sm:w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        backgroundColor: category.color || '#3B82F6',
                        width: `${Math.min(100, ((category._count?.courses || 0) / Math.max(...categories.map(c => c._count?.courses || 0))) * 100)}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};